import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../services/api';
import { STORAGE_KEYS } from '../utils/constants';

const NotificationContext = createContext();

export const NOTIFICATION_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

export const NOTIFICATION_CATEGORIES = {
  PAYMENT: 'payment',
  SIGNUP: 'signup',
  EXTERNAL_ACCOUNT: 'external_account',
  BANK: 'bank',
  KYC: 'kyc',
  PROFILE: 'profile',
  SECURITY: 'security',
  TEAM: 'team'
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch notifications from database on mount (only if authenticated)
  const fetchNotifications = useCallback(async () => {
    // Only fetch if user has a token - prevents 401 errors on public pages
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.get('/notifications');
      const dbNotifications = response.data.notifications.map(n => ({
        id: n._id,
        type: n.type,
        category: n.category,
        title: n.title,
        message: n.message,
        createdAt: new Date(n.createdAt),
        read: n.read,
        isNew: false // Loaded from DB - don't show toast
      }));
      setNotifications(dbNotifications);
    } catch (err) {
      // Silently fail - user might not be logged in
      console.warn('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const addNotification = useCallback(async ({ type = NOTIFICATION_TYPES.INFO, category, title, message }) => {
    // Optimistically add to UI with isNew flag for toast display
    const tempId = Date.now() + Math.random();
    const notification = { id: tempId, type, category, title, message, createdAt: new Date(), isNew: true };
    setNotifications(prev => [notification, ...prev]);
    
    try {
      // Save to database
      const response = await api.post('/notifications', { type, category, title, message });
      // Update with real ID from database
      setNotifications(prev => prev.map(n => 
        n.id === tempId ? { ...n, id: response.data._id } : n
      ));
      return response.data._id;
    } catch (err) {
      console.error('Failed to save notification:', err);
      return tempId;
    }
  }, []);

  const removeNotification = useCallback(async (id) => {
    // Optimistically remove from UI
    setNotifications(prev => prev.filter(n => n.id !== id));
    
    try {
      // Delete from database
      await api.delete(`/notifications/${id}`);
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  }, []);

  const clearAll = useCallback(async () => {
    // Optimistically clear UI
    setNotifications([]);
    
    try {
      // Clear from database
      await api.delete('/notifications');
    } catch (err) {
      console.error('Failed to clear notifications:', err);
    }
  }, []);

  // Shorthand methods
  const notify = {
    // Payment notifications
    paymentSuccess: (message) => addNotification({ type: NOTIFICATION_TYPES.SUCCESS, category: NOTIFICATION_CATEGORIES.PAYMENT, title: 'Payment Received', message }),
    paymentPending: (message) => addNotification({ type: NOTIFICATION_TYPES.WARNING, category: NOTIFICATION_CATEGORIES.PAYMENT, title: 'Payment Pending', message }),
    paymentFailed: (message) => addNotification({ type: NOTIFICATION_TYPES.ERROR, category: NOTIFICATION_CATEGORIES.PAYMENT, title: 'Payment Failed', message }),
    
    // Signup notifications
    signupSuccess: (message) => addNotification({ type: NOTIFICATION_TYPES.SUCCESS, category: NOTIFICATION_CATEGORIES.SIGNUP, title: 'Welcome!', message }),
    signupError: (message) => addNotification({ type: NOTIFICATION_TYPES.ERROR, category: NOTIFICATION_CATEGORIES.SIGNUP, title: 'Sign Up Failed', message }),
    
    // External Account notifications
    walletUpdated: (message) => addNotification({ type: NOTIFICATION_TYPES.SUCCESS, category: NOTIFICATION_CATEGORIES.EXTERNAL_ACCOUNT, title: 'Wallet Updated', message }),
    walletError: (message) => addNotification({ type: NOTIFICATION_TYPES.ERROR, category: NOTIFICATION_CATEGORIES.EXTERNAL_ACCOUNT, title: 'Wallet Error', message }),
    
    // Bank notifications
    bankCreated: (message) => addNotification({ type: NOTIFICATION_TYPES.SUCCESS, category: NOTIFICATION_CATEGORIES.BANK, title: 'Bank Account Created', message }),
    bankPending: (message) => addNotification({ type: NOTIFICATION_TYPES.WARNING, category: NOTIFICATION_CATEGORIES.BANK, title: 'Bank Account Pending', message }),
    bankError: (message) => addNotification({ type: NOTIFICATION_TYPES.ERROR, category: NOTIFICATION_CATEGORIES.BANK, title: 'Bank Account Error', message }),
    
    // KYC notifications
    kycSubmitted: (message) => addNotification({ type: NOTIFICATION_TYPES.INFO, category: NOTIFICATION_CATEGORIES.KYC, title: 'KYC Submitted', message }),
    kycApproved: (message) => addNotification({ type: NOTIFICATION_TYPES.SUCCESS, category: NOTIFICATION_CATEGORIES.KYC, title: 'KYC Approved', message }),
    kycRejected: (message) => addNotification({ type: NOTIFICATION_TYPES.ERROR, category: NOTIFICATION_CATEGORIES.KYC, title: 'KYC Rejected', message }),
    kycPending: (message) => addNotification({ type: NOTIFICATION_TYPES.WARNING, category: NOTIFICATION_CATEGORIES.KYC, title: 'KYC Under Review', message }),
    
    // Profile notifications
    profileUpdated: (message) => addNotification({ type: NOTIFICATION_TYPES.SUCCESS, category: NOTIFICATION_CATEGORIES.PROFILE, title: 'Profile Updated', message }),
    profileError: (message) => addNotification({ type: NOTIFICATION_TYPES.ERROR, category: NOTIFICATION_CATEGORIES.PROFILE, title: 'Profile Update Failed', message }),
    
    // Security notifications
    passwordChanged: (message) => addNotification({ type: NOTIFICATION_TYPES.SUCCESS, category: NOTIFICATION_CATEGORIES.SECURITY, title: 'Password Changed', message }),
    twoFactorEnabled: (message) => addNotification({ type: NOTIFICATION_TYPES.SUCCESS, category: NOTIFICATION_CATEGORIES.SECURITY, title: '2FA Enabled', message }),
    twoFactorDisabled: (message) => addNotification({ type: NOTIFICATION_TYPES.WARNING, category: NOTIFICATION_CATEGORIES.SECURITY, title: '2FA Disabled', message }),
    securityAlert: (message) => addNotification({ type: NOTIFICATION_TYPES.ERROR, category: NOTIFICATION_CATEGORIES.SECURITY, title: 'Security Alert', message }),
    
    // Team notifications
    teamMemberCreated: (message) => addNotification({ type: NOTIFICATION_TYPES.INFO, category: NOTIFICATION_CATEGORIES.TEAM, title: 'New Team Member', message }),
    teamMemberRemoved: (message) => addNotification({ type: NOTIFICATION_TYPES.WARNING, category: NOTIFICATION_CATEGORIES.TEAM, title: 'Team Member Removed', message }),
    teamMemberAccessChanged: (message) => addNotification({ type: NOTIFICATION_TYPES.INFO, category: NOTIFICATION_CATEGORIES.TEAM, title: 'Access Changed', message }),
    teamMemberKycUpdated: (message) => addNotification({ type: NOTIFICATION_TYPES.INFO, category: NOTIFICATION_CATEGORIES.TEAM, title: 'KYC Status Updated', message }),
    teamMemberPaymentCompleted: (message) => addNotification({ type: NOTIFICATION_TYPES.SUCCESS, category: NOTIFICATION_CATEGORIES.TEAM, title: 'Payment Completed', message }),
    
    // Generic
    success: (title, message) => addNotification({ type: NOTIFICATION_TYPES.SUCCESS, title, message }),
    error: (title, message) => addNotification({ type: NOTIFICATION_TYPES.ERROR, title, message }),
    warning: (title, message) => addNotification({ type: NOTIFICATION_TYPES.WARNING, title, message }),
    info: (title, message) => addNotification({ type: NOTIFICATION_TYPES.INFO, title, message })
  };

  return (
    <NotificationContext.Provider value={{ notifications, notify, addNotification, removeNotification, clearAll, loading, refetch: fetchNotifications }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotification must be used within NotificationProvider');
  return context;
};

